import { NodeViewProps, NodeViewWrapper } from "@tiptap/react";
import { ActionIcon, Button, Group, Select, TextInput, Textarea, Tooltip } from "@mantine/core";
import { IconPlus, IconX } from "@tabler/icons-react";
import classes from "./api-reference-v2.module.css";

const METHODS = ["get", "post", "put", "patch", "delete"] as const;
type Method = (typeof METHODS)[number];

const METHOD_BG: Record<Method, string> = {
  get: "#3b82f6",
  post: "#10b981",
  put: "#eab308",
  patch: "#f97316",
  delete: "#ef4444",
};

function normalizeMethod(v: unknown): Method {
  if (typeof v !== "string") return "get";
  const lower = v.toLowerCase() as Method;
  return (METHODS as readonly string[]).includes(lower) ? lower : "get";
}

export default function ApiReferenceV2View(props: NodeViewProps) {
  const { node, editor, updateAttributes, selected } = props;
  const method = normalizeMethod(node.attrs.method);
  const path = typeof node.attrs.path === "string" ? node.attrs.path : "";
  const title = typeof node.attrs.title === "string" ? node.attrs.title : "";
  const description =
    typeof node.attrs.description === "string" ? node.attrs.description : "";
  const useWhen =
    typeof node.attrs.useWhen === "string" ? node.attrs.useWhen : "";
  // Visibility flags default to true so legacy nodes (saved before the
  // flags existed) keep showing every field.
  const showTitle = node.attrs.showTitle !== false;
  const showDescription = node.attrs.showDescription !== false;
  const showUseWhen = node.attrs.showUseWhen !== false;
  const canEdit = editor.isEditable;

  const removeBtn = (onClick: () => void, label: string) => (
    <Tooltip label={label} withArrow>
      <ActionIcon
        variant="subtle"
        color="gray"
        size="sm"
        onClick={onClick}
        aria-label={label}
      >
        <IconX size={14} />
      </ActionIcon>
    </Tooltip>
  );

  return (
    <NodeViewWrapper
      className={classes.wrapper}
      data-type="api-reference-v2"
      data-method={method}
      data-selected={selected ? "true" : undefined}
    >
      <div className={classes.card}>
        <div className={classes.headerRow}>
          {canEdit ? (
            <Select
              size="xs"
              w={110}
              value={method}
              data={METHODS.map((m) => ({ value: m, label: m.toUpperCase() }))}
              onChange={(v) => updateAttributes({ method: normalizeMethod(v) })}
              styles={{
                input: {
                  backgroundColor: METHOD_BG[method],
                  color: "#fff",
                  fontWeight: 700,
                  border: "none",
                },
              }}
            />
          ) : (
            <span
              className={classes.badge}
              style={{ backgroundColor: METHOD_BG[method] }}
            >
              {method.toUpperCase()}
            </span>
          )}
          <TextInput
            size="xs"
            className={classes.path}
            placeholder="/auth/{id}"
            value={path}
            readOnly={!canEdit}
            onChange={(e) =>
              updateAttributes({ path: e.currentTarget.value })
            }
            styles={{ input: { fontFamily: "ui-monospace, monospace" } }}
          />
        </div>

        {showTitle && (
          <Group gap={4} wrap="nowrap" align="flex-start">
            <TextInput
              className={classes.title}
              size="sm"
              placeholder="Title (e.g. List authorizations)"
              value={title}
              readOnly={!canEdit}
              onChange={(e) =>
                updateAttributes({ title: e.currentTarget.value })
              }
              variant="unstyled"
              style={{ flex: 1 }}
            />
            {canEdit &&
              removeBtn(
                () => updateAttributes({ title: "", showTitle: false }),
                "Remove title",
              )}
          </Group>
        )}

        {showDescription && (
          <Group gap={4} wrap="nowrap" align="flex-start">
            <Textarea
              size="sm"
              placeholder="Description"
              value={description}
              readOnly={!canEdit}
              autosize
              minRows={1}
              onChange={(e) =>
                updateAttributes({ description: e.currentTarget.value })
              }
              variant="unstyled"
              style={{ flex: 1 }}
            />
            {canEdit &&
              removeBtn(
                () =>
                  updateAttributes({
                    description: "",
                    showDescription: false,
                  }),
                "Remove description",
              )}
          </Group>
        )}

        {showUseWhen && (
          <>
            <Group gap={4} wrap="nowrap" align="center" justify="space-between">
              <div className={classes.fieldLabel}>Use when:</div>
              {canEdit &&
                removeBtn(
                  () =>
                    updateAttributes({ useWhen: "", showUseWhen: false }),
                  "Remove Use when",
                )}
            </Group>
            <Textarea
              size="sm"
              placeholder="auditing all data tied to a client email."
              value={useWhen}
              readOnly={!canEdit}
              autosize
              minRows={1}
              onChange={(e) =>
                updateAttributes({ useWhen: e.currentTarget.value })
              }
              variant="unstyled"
            />
          </>
        )}

        {canEdit && (!showTitle || !showDescription || !showUseWhen) && (
          <Group gap={6} mt={4}>
            {!showTitle && (
              <Button
                size="compact-xs"
                variant="light"
                color="gray"
                leftSection={<IconPlus size={12} />}
                onClick={() => updateAttributes({ showTitle: true })}
              >
                Title
              </Button>
            )}
            {!showDescription && (
              <Button
                size="compact-xs"
                variant="light"
                color="gray"
                leftSection={<IconPlus size={12} />}
                onClick={() => updateAttributes({ showDescription: true })}
              >
                Description
              </Button>
            )}
            {!showUseWhen && (
              <Button
                size="compact-xs"
                variant="light"
                color="gray"
                leftSection={<IconPlus size={12} />}
                onClick={() => updateAttributes({ showUseWhen: true })}
              >
                Use when
              </Button>
            )}
          </Group>
        )}
      </div>
    </NodeViewWrapper>
  );
}
